import { RE2JS } from 're2js';
import { REGEX_PATTERN_MAX_LENGTH, REGEX_TEST_TEXT_MAX_LENGTH } from '@/lib/regex-helpers';

export type RegexHighlightSegment = {
  text: string;
  isMatch: boolean;
};

export type RegexHighlightResult = {
  segments: RegexHighlightSegment[];
  reason?: string;
};

const REGEX_HIGHLIGHT_MAX_MATCH_ITERATIONS = 100;
const TOO_MANY_MATCHES_REASON =
  'This pattern produced too many matches to preview. Try a more specific pattern.';
const INVALID_RE2_REASON =
  'This pattern is not valid for the detection engine (RE2), so it will never match. Fix the pattern to preview matches.';

// Uses re2js - same as backend so preview reflects real detection behaviour and blocks patterns RE2 rejects. re2js matches in linear time, so unlike the browser's RegExp it cannot freeze the tab, and no ReDoS pre-check is needed.
export const computeRegexHighlightSegments = (
  text: string,
  regexPattern: string
): RegexHighlightResult => {
  if (!text) {
    return { segments: [] };
  }
  if (!regexPattern?.trim()) {
    return { segments: [{ text, isMatch: false }] };
  }

  let compiled: RE2JS;
  try {
    compiled = RE2JS.compile(regexPattern);
  } catch {
    return { segments: [{ text, isMatch: false }], reason: INVALID_RE2_REASON };
  }

  const segments: RegexHighlightSegment[] = [];
  let lastIndex = 0;
  let iterationCount = 0;
  for (const match of compiled.matchAll(text)) {
    iterationCount += 1;
    if (iterationCount > REGEX_HIGHLIGHT_MAX_MATCH_ITERATIONS) {
      return { segments: [{ text, isMatch: false }], reason: TOO_MANY_MATCHES_REASON };
    }
    const matched = match[0];
    if (!matched) {
      continue; // zero-width match: nothing to highlight
    }
    const start = match.index ?? 0;
    if (start > lastIndex) {
      segments.push({ text: text.slice(lastIndex, start), isMatch: false });
    }
    segments.push({ text: matched, isMatch: true });
    lastIndex = start + matched.length;
  }
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), isMatch: false });
  }
  return { segments };
};

export const runRegexHighlightSafely = (
  rawText: string,
  rawPattern: string
): RegexHighlightResult => {
  const text = rawText.slice(0, REGEX_TEST_TEXT_MAX_LENGTH);
  const pattern = rawPattern.slice(0, REGEX_PATTERN_MAX_LENGTH);

  if (!text) {
    return { segments: [] };
  }
  if (!pattern.trim()) {
    return { segments: [{ text, isMatch: false }] };
  }
  return computeRegexHighlightSegments(text, pattern);
};
